import { useTheme } from '@emotion/react';
import { useMemo } from 'react';

import type { SerializedStyles, Theme, Interpolation, ArrayInterpolation } from '@emotion/react';

type Classes<T extends Theme = Theme, CN extends string = string> = Record<
  CN,
  SerializedStyles | ((theme: T) => SerializedStyles)
>;

export type ClassesPropOf<UseStyles extends UseStylesType> = Partial<
  Record<keyof ReturnType<UseStyles>, Interpolation<Theme>>
>;

/**
 * // TODO: @docs Add documentation
 */
export const makeStyles =
  <T extends Classes>(classesFn: T | ((props: { theme: Theme }) => T)) =>
  (props?: { classes?: Partial<Record<keyof T, Interpolation<Theme>>> }): ClsMerge<T> => {
    const theme = useTheme();
    const classes = typeof classesFn === 'function' ? classesFn({ ...props, theme }) : classesFn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => mergeClasses(classes, props?.classes), [props?.classes]);
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UseStylesType = (props: any) => Record<string, any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ClsMerge<T extends Record<string, any>> = { [K in keyof T]: ArrayInterpolation<Theme> };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mergeClasses<T extends Record<string, any>>(
  ...clss: [T, ...(Partial<T> | undefined)[]]
): ClsMerge<T> {
  return clss.reduce((acc, cls) => {
    if (!cls) return acc;

    for (const className in cls) {
      if (!acc[className]) acc[className] = [];

      acc[className].push(cls[className] as T[Extract<keyof T, string>]);
    }

    return acc;
  }, {} as ClsMerge<T>);
}
